"use client";

import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type ListNotificationsParams,
} from "@/lib/api/endpoints/notifications";
import { env } from "@/lib/env";
import { queryClient } from "@/lib/query-client";
import type { NotificationDto, NotificationListDto } from "@/lib/api/types";
import { useAuthStore } from "@/modules/auth/store";

type NotificationScopeKey = readonly [
  "notifications",
  string,
  string,
  string,
];

function currentNotificationScopeKey(): NotificationScopeKey {
  const { user, tenantId, impersonation } = useAuthStore.getState();
  return [
    "notifications",
    user?.id ?? "anonymous",
    tenantId ?? "no-tenant",
    impersonation?.started_at ?? "direct",
  ] as const;
}

export const notificationQueryKeys = {
  all: ["notifications"] as const,
  scope: (scope: NotificationScopeKey = currentNotificationScopeKey()) => scope,
  list: (scope: NotificationScopeKey, params: ListNotificationsParams) => [...scope, "list", params] as const,
};

function updateNotificationLists(
  scope: NotificationScopeKey,
  updater: (
    current: NotificationListDto | undefined,
    params: ListNotificationsParams,
  ) => NotificationListDto | undefined,
) {
  const queries = queryClient.getQueryCache().findAll({ queryKey: notificationQueryKeys.scope(scope) });
  for (const query of queries) {
    const key = query.queryKey;
    if (!Array.isArray(key) || key[4] !== "list") {
      continue;
    }
    const params = (key[5] ?? {}) as ListNotificationsParams;
    queryClient.setQueryData<NotificationListDto>(key, (current) => updater(current, params));
  }
}

function readNotificationSearchText(notification: NotificationDto): string {
  const payload = notification.payload ?? {};
  return [
    notification.title,
    notification.body,
    notification.type,
    payload.device_name,
    payload.device_uid,
    payload.device_serial_number,
    payload.rule_name,
    payload.message,
  ]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join(" ")
    .toLowerCase();
}

function notificationMatchesParams(notification: NotificationDto, params: ListNotificationsParams): boolean {
  if (params.readState === "read" && !notification.is_read) {
    return false;
  }
  if (params.readState === "unread" && notification.is_read) {
    return false;
  }
  if (params.severity && params.severity !== "all") {
    const severity = typeof notification.payload?.severity === "string" ? notification.payload.severity.toLowerCase() : "";
    if (severity !== params.severity) {
      return false;
    }
  }
  if (params.type?.trim() && notification.type !== params.type.trim()) {
    return false;
  }
  if (params.search?.trim()) {
    return readNotificationSearchText(notification).includes(params.search.trim().toLowerCase());
  }
  return true;
}

function mergeIncomingNotification(
  current: NotificationListDto | undefined,
  notification: NotificationDto,
  params: ListNotificationsParams,
): NotificationListDto | undefined {
  const matches = notificationMatchesParams(notification, params);
  if (!current) {
    if (!matches) {
      return current;
    }
    return {
      items: [notification],
      total: 1,
      unread_count: notification.is_read ? 0 : 1,
    };
  }
  if (current.items.some((item) => item.id === notification.id)) {
    return current;
  }
  const isFirstPage = (params.offset ?? 0) === 0;
  const nextItems = matches && isFirstPage ? [notification, ...current.items] : current.items;
  const cap = current.items.length > 0 ? current.items.length : nextItems.length;
  return {
    ...current,
    items: nextItems.slice(0, cap),
    total: current.total + (matches ? 1 : 0),
    unread_count: current.unread_count + (notification.is_read ? 0 : 1),
  };
}

function applyReadState(
  current: NotificationListDto | undefined,
  notificationId: string | null,
  markAll: boolean,
  params: ListNotificationsParams,
): NotificationListDto | undefined {
  if (!current) {
    return current;
  }
  let unreadRemoved = 0;
  let totalRemoved = 0;
  const items = current.items.flatMap((item) => {
    const shouldMark = markAll || item.id === notificationId;
    if (!shouldMark || item.is_read) {
      return [item];
    }
    unreadRemoved += 1;
    if (markAll || params.readState === "unread") {
      totalRemoved += params.readState === "unread" ? 1 : 0;
    }
    if (params.readState === "unread") {
      return [];
    }
    return {
      ...item,
      is_read: true,
      read_at: new Date().toISOString(),
    };
  });
  return {
    ...current,
    items,
    total: markAll && params.readState === "unread" ? 0 : Math.max(0, current.total - totalRemoved),
    unread_count: markAll ? 0 : Math.max(0, current.unread_count - (notificationId ? 1 : unreadRemoved)),
  };
}

export function useNotificationsQuery(params: ListNotificationsParams, enabled = true) {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const tenantId = useAuthStore((s) => s.tenantId);
  const impersonationStartedAt = useAuthStore((s) => s.impersonation?.started_at ?? null);
  const scope = [
    "notifications",
    userId ?? "anonymous",
    tenantId ?? "no-tenant",
    impersonationStartedAt ?? "direct",
  ] as const satisfies NotificationScopeKey;

  return useQuery({
    queryKey: notificationQueryKeys.list(scope, params),
    queryFn: () => listNotifications(params),
    enabled,
    staleTime: 20_000,
  });
}

export function useMarkNotificationRead() {
  return useMutation({
    mutationFn: (notificationId: string) => markNotificationRead(notificationId),
    onSuccess: (updated) => {
      updateNotificationLists(currentNotificationScopeKey(), (current, params) => applyReadState(current, updated.id, false, params));
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.scope(), refetchType: "active" });
    },
  });
}

export function useMarkAllNotificationsRead() {
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      updateNotificationLists(currentNotificationScopeKey(), (current, params) => applyReadState(current, null, true, params));
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.scope(), refetchType: "active" });
    },
  });
}

export function useNotificationStream(enabled = true) {
  const token = useAuthStore((s) => s.token);
  const tenantId = useAuthStore((s) => s.tenantId);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const impersonationStartedAt = useAuthStore((s) => s.impersonation?.started_at ?? null);

  useEffect(() => {
    if (!enabled || !token || !tenantId || typeof window === "undefined") {
      return;
    }

    const base = env.publicApiUrl || env.backendApiUrl || window.location.origin;
    const url = new URL(`${base}/notifications/stream`);
    url.searchParams.set("tenant_id", tenantId);

    const scope = currentNotificationScopeKey();
    const source = new EventSource(url.toString(), { withCredentials: true });
    source.addEventListener("notification", (event) => {
      try {
        const parsed = JSON.parse((event as MessageEvent<string>).data) as {
          notification?: NotificationDto;
        };
        if (!parsed.notification) {
          return;
        }
        updateNotificationLists(scope, (current, params) => mergeIncomingNotification(current, parsed.notification!, params));
        void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.scope(scope), refetchType: "active" });
      } catch {
        // Ignore malformed payloads and keep listening.
      }
    });

    return () => {
      source.close();
    };
  }, [enabled, impersonationStartedAt, tenantId, token, userId]);
}

export type { ListNotificationsParams } from "@/lib/api/endpoints/notifications";
